import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TableCell, TableRow } from '../ui/table'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import type { AdminSettingListEntry } from '../../hooks/use-admin-settings'
import { useAdminSettingPatch } from '../../hooks/use-admin-settings'
import { kindBadgeVariant, stageBadgeVariant, visibilityBadgeVariant, isStale } from './settings-registry-badges'
import { settingErrorDetail } from '../feature-flags/FeatureFlagsManagementPage'

const JSON_PREVIEW_MAX = 60
const STAGE_VALUES = ['superadmin', 'org', 'server'] as const
const VISIBILITY_VALUES = ['public', 'internal', 'private', 'system'] as const

function jsonPreview(value: unknown): string {
    if (value === undefined) return '—'
    const text = JSON.stringify(value)
    if (text.length <= JSON_PREVIEW_MAX) return text
    return text.slice(0, JSON_PREVIEW_MAX - 1) + '…'
}

function isBoolean(v: unknown): v is boolean {
    return typeof v === 'boolean'
}

type Notice = { kind: 'success' | 'error'; text: string } | null

interface RowProps {
    entry: AdminSettingListEntry
    onViewOverrides: () => void
    onNotice: (n: Notice) => void
}

export function SettingsRegistryRow({ entry, onViewOverrides, onNotice }: RowProps) {
    const { t, i18n } = useTranslation('admin')
    const patch = useAdminSettingPatch()

    const name = entry.name?.[i18n.language] ?? entry.name?.en ?? entry.code
    const stale = isStale(entry.last_read_at)

    // --- Stage promotion (flag-kind) ---
    const [pendingStage, setPendingStage] = useState<string | null>(null)

    const applyStage = async (newStage: string) => {
        try {
            await patch.mutateAsync({
                params: { path: { code: entry.code } },
                body: { stage: newStage as 'superadmin' | 'org' | 'server' },
            })
            onNotice({ kind: 'success', text: t('registry.notice.saved', { code: entry.code }) })
        } catch (err) {
            const detail = settingErrorDetail(err)
            onNotice({
                kind: 'error',
                text: detail
                    ? t('registry.notice.save_failed_detail', { code: entry.code, detail })
                    : t('registry.notice.save_failed', { code: entry.code }),
            })
        } finally {
            setPendingStage(null)
        }
    }

    // --- Server value editing ---
    const [editingServer, setEditingServer] = useState(false)
    const [serverJsonText, setServerJsonText] = useState('')
    const [serverJsonError, setServerJsonError] = useState<string | null>(null)
    const [confirmClearServer, setConfirmClearServer] = useState(false)

    useEffect(() => {
        setServerJsonText(entry.server_value !== undefined ? JSON.stringify(entry.server_value, null, 2) : '')
    }, [entry.server_value])

    const serverBoolValue = isBoolean(entry.server_value) ? entry.server_value : null

    const savePatch = async (body: Record<string, unknown>, successKey = 'registry.notice.saved') => {
        try {
            await patch.mutateAsync({ params: { path: { code: entry.code } }, body })
            onNotice({ kind: 'success', text: t(successKey, { code: entry.code }) })
            return true
        } catch (err) {
            const detail = settingErrorDetail(err)
            onNotice({
                kind: 'error',
                text: detail
                    ? t('registry.notice.save_failed_detail', { code: entry.code, detail })
                    : t('registry.notice.save_failed', { code: entry.code }),
            })
            return false
        }
    }

    const handleToggleServerBool = async () => {
        if (serverBoolValue === null) return
        await savePatch({ server_value: !serverBoolValue })
    }

    const handleSaveServerJson = async () => {
        let parsed: unknown
        try {
            parsed = JSON.parse(serverJsonText)
        } catch {
            setServerJsonError(t('registry.editor.invalid_json'))
            return
        }
        setServerJsonError(null)
        if (await savePatch({ server_value: parsed })) {
            setEditingServer(false)
        }
    }

    const handleClearServer = async () => {
        await savePatch({ server_value: null })
        setConfirmClearServer(false)
    }

    // --- Setting-kind extras: visibility select ---
    const handleVisibilityChange = async (visibility: string) => {
        await savePatch({ visibility })
    }

    // --- Setting-kind extras: default value editor ---
    const [editingDefault, setEditingDefault] = useState(false)
    const [defaultJsonText, setDefaultJsonText] = useState('')
    const [defaultJsonError, setDefaultJsonError] = useState<string | null>(null)

    useEffect(() => {
        setDefaultJsonText(entry.default_value !== undefined ? JSON.stringify(entry.default_value, null, 2) : '')
    }, [entry.default_value])

    const handleSaveDefaultJson = async () => {
        let parsed: unknown
        try {
            parsed = JSON.parse(defaultJsonText)
        } catch {
            setDefaultJsonError(t('registry.editor.invalid_json'))
            return
        }
        setDefaultJsonError(null)
        if (await savePatch({ default_value: parsed })) {
            setEditingDefault(false)
        }
    }

    // --- Setting-kind extras: jsonschema editor ---
    const [editingSchema, setEditingSchema] = useState(false)
    const [schemaJsonText, setSchemaJsonText] = useState('')
    const [schemaJsonError, setSchemaJsonError] = useState<string | null>(null)

    useEffect(() => {
        setSchemaJsonText(entry.jsonschema !== undefined && entry.jsonschema !== null ? JSON.stringify(entry.jsonschema, null, 2) : '')
    }, [entry])

    const handleSaveSchemaJson = async () => {
        let parsed: unknown
        try {
            parsed = schemaJsonText.trim() === '' ? null : JSON.parse(schemaJsonText)
        } catch {
            setSchemaJsonError(t('registry.editor.invalid_json'))
            return
        }
        setSchemaJsonError(null)
        if (await savePatch({ jsonschema: parsed })) {
            setEditingSchema(false)
        }
    }

    // --- Credential (write-only secret) editing ---
    // The value is never returned by the API; we only ever send a new value or
    // clear it. `configured` reports presence only.
    const [credentialInput, setCredentialInput] = useState('')
    const [editingCredential, setEditingCredential] = useState(false)
    const [confirmClearCredential, setConfirmClearCredential] = useState(false)

    const handleSaveCredential = async () => {
        if (credentialInput.trim() === '') return
        if (await savePatch({ server_value: credentialInput })) {
            setCredentialInput('')
            setEditingCredential(false)
        }
    }

    const handleClearCredential = async () => {
        await savePatch({ server_value: null })
        setConfirmClearCredential(false)
        setCredentialInput('')
        setEditingCredential(false)
    }

    if (entry.credential) {
        const showInput = editingCredential || !entry.configured
        return (
            <TableRow data-testid={`settings-registry-row-${entry.code}`}>
                <TableCell>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="font-mono text-sm text-foreground cursor-default" data-testid={`settings-registry-code-${entry.code}`}>
                                    {entry.code}
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <div className="max-w-xs">
                                    <div className="font-medium">{name}</div>
                                    {entry.description && <div className="text-xs mt-1">{entry.description}</div>}
                                </div>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </TableCell>
                <TableCell>
                    <div className="flex items-center gap-1">
                        <Badge variant={kindBadgeVariant(entry.kind)} data-testid={`settings-registry-kind-badge-${entry.code}`}>
                            {t('registry.kind.setting')}
                        </Badge>
                        <Badge variant="secondary" data-testid={`settings-registry-credential-badge-${entry.code}`}>
                            {t('registry.credential.badge')}
                        </Badge>
                    </div>
                </TableCell>
                <TableCell>
                    {entry.visibility && (
                        <Badge variant={visibilityBadgeVariant(entry.visibility)} data-testid={`settings-registry-visibility-${entry.code}`}>
                            {t(`registry.visibility.${entry.visibility}`)}
                        </Badge>
                    )}
                </TableCell>
                <TableCell>
                    <span className="text-xs text-muted-foreground" data-testid={`settings-registry-default-${entry.code}`}>—</span>
                </TableCell>
                <TableCell>
                    {showInput ? (
                        <div className="min-w-48" data-testid={`settings-registry-credential-editor-${entry.code}`}>
                            <Input
                                type="password"
                                autoComplete="new-password"
                                value={credentialInput}
                                onChange={(e) => setCredentialInput(e.target.value)}
                                placeholder={t('registry.credential.input_placeholder')}
                                className="font-mono text-xs"
                                data-testid={`settings-registry-credential-input-${entry.code}`}
                            />
                            <div className="flex gap-1 mt-1">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={patch.isPending || credentialInput.trim() === ''}
                                    onClick={handleSaveCredential}
                                    data-testid={`settings-registry-credential-save-${entry.code}`}
                                >
                                    {t('registry.action.save')}
                                </Button>
                                {entry.configured && (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => { setEditingCredential(false); setCredentialInput('') }}
                                        data-testid={`settings-registry-credential-cancel-${entry.code}`}
                                    >
                                        {t('registry.action.cancel')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-muted-foreground select-none" data-testid={`settings-registry-credential-mask-${entry.code}`}>
                                ••••••••
                            </span>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingCredential(true)}
                                data-testid={`settings-registry-credential-replace-${entry.code}`}
                            >
                                {t('registry.credential.replace')}
                            </Button>
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => setConfirmClearCredential(true)}
                                aria-label={t('registry.credential.clear')}
                                title={t('registry.credential.clear')}
                                data-testid={`settings-registry-credential-clear-${entry.code}`}
                            >
                                ×
                            </Button>
                        </div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1" data-testid={`settings-registry-credential-status-${entry.code}`}>
                        {entry.configured ? t('registry.credential.configured') : t('registry.credential.not_configured')}
                    </div>

                    <AlertDialog open={confirmClearCredential} onOpenChange={setConfirmClearCredential}>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>{t('registry.credential.clear_confirm_title')}</AlertDialogTitle>
                                <AlertDialogDescription>
                                    {t('registry.credential.clear_confirm_description', { code: entry.code })}
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel data-testid={`settings-registry-credential-clear-cancel-${entry.code}`}>
                                    {t('registry.action.cancel')}
                                </AlertDialogCancel>
                                <AlertDialogAction
                                    onClick={handleClearCredential}
                                    data-testid={`settings-registry-credential-clear-confirm-${entry.code}`}
                                >
                                    {t('registry.credential.clear')}
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </TableCell>
                <TableCell className="text-right">
                    <span className="text-xs text-muted-foreground" data-testid={`settings-registry-overrides-${entry.code}`}>—</span>
                </TableCell>
                <TableCell>
                    {stale ? (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Badge variant="warning" data-testid={`settings-registry-stale-${entry.code}`}>
                                        {t('registry.stale_badge')}
                                    </Badge>
                                </TooltipTrigger>
                                <TooltipContent>{t('registry.stale_tooltip')}</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    ) : (
                        <span className="text-xs text-muted-foreground" data-testid={`settings-registry-last-read-${entry.code}`}>
                            {entry.last_read_at ? new Date(entry.last_read_at).toLocaleDateString(i18n.language) : t('registry.never_read')}
                        </span>
                    )}
                </TableCell>
            </TableRow>
        )
    }

    return (
        <TableRow data-testid={`settings-registry-row-${entry.code}`}>
            <TableCell>
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="font-mono text-sm text-foreground cursor-default" data-testid={`settings-registry-code-${entry.code}`}>
                                {entry.code}
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>
                            <div className="max-w-xs">
                                <div className="font-medium">{name}</div>
                                {entry.description && <div className="text-xs mt-1">{entry.description}</div>}
                            </div>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </TableCell>
            <TableCell>
                <Badge variant={kindBadgeVariant(entry.kind)} data-testid={`settings-registry-kind-badge-${entry.code}`}>
                    {t(`registry.kind.${entry.kind}`)}
                </Badge>
            </TableCell>
            <TableCell>
                {entry.kind === 'flag' && entry.stage && (
                    <Select value={entry.stage} onValueChange={setPendingStage} disabled={patch.isPending}>
                        <SelectTrigger className="h-7 w-auto" data-testid={`settings-registry-stage-select-${entry.code}`}>
                            <Badge variant={stageBadgeVariant(entry.stage)} data-testid={`settings-registry-stage-${entry.code}`}>
                                <SelectValue />
                            </Badge>
                        </SelectTrigger>
                        <SelectContent>
                            {STAGE_VALUES.map((s) => (
                                <SelectItem key={s} value={s} data-testid={`settings-registry-stage-option-${entry.code}-${s}`}>
                                    {t(`registry.stage.${s}`)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
                {entry.kind === 'setting' && entry.visibility && (
                    <Select value={entry.visibility} onValueChange={handleVisibilityChange} disabled={patch.isPending}>
                        <SelectTrigger className="h-7 w-auto" data-testid={`settings-registry-visibility-select-${entry.code}`}>
                            <Badge variant={visibilityBadgeVariant(entry.visibility)} data-testid={`settings-registry-visibility-${entry.code}`}>
                                <SelectValue />
                            </Badge>
                        </SelectTrigger>
                        <SelectContent>
                            {VISIBILITY_VALUES.map((v) => (
                                <SelectItem key={v} value={v} data-testid={`settings-registry-visibility-option-${entry.code}-${v}`}>
                                    {t(`registry.visibility.${v}`)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}

                <AlertDialog open={!!pendingStage} onOpenChange={(open) => { if (!open) setPendingStage(null) }}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>{t('registry.stage_dialog.title')}</AlertDialogTitle>
                            <AlertDialogDescription>
                                {t('registry.stage_dialog.description', { code: entry.code })}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel data-testid={`settings-registry-stage-cancel-${entry.code}`}>
                                {t('registry.action.cancel')}
                            </AlertDialogCancel>
                            <AlertDialogAction
                                onClick={() => pendingStage && applyStage(pendingStage)}
                                data-testid={`settings-registry-stage-confirm-${entry.code}`}
                            >
                                {t('registry.stage_dialog.confirm')}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </TableCell>
            <TableCell>
                {entry.kind === 'setting' && entry.server_scoped ? (
                    <code
                        className="text-xs text-muted-foreground"
                        title={t('registry.editor.server_default_source')}
                        data-testid={`settings-registry-default-${entry.code}`}
                    >
                        {t('registry.editor.server_default_source')}
                    </code>
                ) : entry.kind === 'setting' ? (
                    editingDefault ? (
                        <div className="min-w-48" data-testid={`settings-registry-default-editor-${entry.code}`}>
                            <Textarea
                                value={defaultJsonText}
                                onChange={(e) => setDefaultJsonText(e.target.value)}
                                rows={3}
                                className="font-mono text-xs"
                                data-testid={`settings-registry-default-json-${entry.code}`}
                            />
                            {defaultJsonError && (
                                <p className="text-xs text-destructive mt-1">{defaultJsonError}</p>
                            )}
                            <div className="flex gap-1 mt-1">
                                <Button size="sm" variant="outline" disabled={patch.isPending} onClick={handleSaveDefaultJson} data-testid={`settings-registry-default-save-${entry.code}`}>
                                    {t('registry.action.save')}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingDefault(false)} data-testid={`settings-registry-default-cancel-${entry.code}`}>
                                    {t('registry.action.cancel')}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <code
                            className="text-xs text-muted-foreground cursor-pointer hover:underline"
                            onClick={() => setEditingDefault(true)}
                            data-testid={`settings-registry-default-${entry.code}`}
                        >
                            {jsonPreview(entry.default_value)}
                        </code>
                    )
                ) : (
                    <code className="text-xs text-muted-foreground" data-testid={`settings-registry-default-${entry.code}`}>
                        {jsonPreview(entry.default_value)}
                    </code>
                )}
            </TableCell>
            <TableCell>
                {editingServer ? (
                    <div className="min-w-48" data-testid={`settings-registry-server-editor-${entry.code}`}>
                        <Textarea
                            value={serverJsonText}
                            onChange={(e) => setServerJsonText(e.target.value)}
                            rows={3}
                            className="font-mono text-xs"
                            data-testid={`settings-registry-server-json-${entry.code}`}
                        />
                        {serverJsonError && <p className="text-xs text-destructive mt-1">{serverJsonError}</p>}
                        <div className="flex gap-1 mt-1">
                            <Button size="sm" variant="outline" disabled={patch.isPending} onClick={handleSaveServerJson} data-testid={`settings-registry-server-save-${entry.code}`}>
                                {t('registry.action.save')}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingServer(false)} data-testid={`settings-registry-server-cancel-${entry.code}`}>
                                {t('registry.action.cancel')}
                            </Button>
                        </div>
                    </div>
                ) : serverBoolValue !== null ? (
                    <div className="flex items-center gap-1">
                        <Button
                            variant={serverBoolValue ? 'default' : 'outline'}
                            size="sm"
                            disabled={patch.isPending}
                            onClick={handleToggleServerBool}
                            data-testid={`settings-registry-server-toggle-${entry.code}`}
                        >
                            {serverBoolValue ? t('registry.editor.on') : t('registry.editor.off')}
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setConfirmClearServer(true)}
                            aria-label={t('registry.editor.use_default')}
                            title={t('registry.editor.use_default')}
                            data-testid={`settings-registry-server-clear-${entry.code}`}
                        >
                            ×
                        </Button>
                    </div>
                ) : (
                    <div className="flex items-center gap-1">
                        <code
                            className="text-xs text-foreground cursor-pointer hover:underline"
                            onClick={() => setEditingServer(true)}
                            data-testid={`settings-registry-server-${entry.code}`}
                        >
                            {jsonPreview(entry.server_value)}
                        </code>
                        {entry.server_value !== undefined && (
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setConfirmClearServer(true)}
                                aria-label={t('registry.editor.use_default')}
                                title={t('registry.editor.use_default')}
                                data-testid={`settings-registry-server-clear-${entry.code}`}
                            >
                                ×
                            </Button>
                        )}
                    </div>
                )}

                {entry.kind === 'setting' && (
                    <div className="mt-1">
                        {editingSchema ? (
                            <div className="min-w-48" data-testid={`settings-registry-schema-editor-${entry.code}`}>
                                <Textarea
                                    value={schemaJsonText}
                                    onChange={(e) => setSchemaJsonText(e.target.value)}
                                    rows={3}
                                    placeholder="{}"
                                    className="font-mono text-xs"
                                    data-testid={`settings-registry-schema-json-${entry.code}`}
                                />
                                {schemaJsonError && <p className="text-xs text-destructive mt-1">{schemaJsonError}</p>}
                                <div className="flex gap-1 mt-1">
                                    <Button size="sm" variant="outline" disabled={patch.isPending} onClick={handleSaveSchemaJson} data-testid={`settings-registry-schema-save-${entry.code}`}>
                                        {t('registry.action.save')}
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => setEditingSchema(false)} data-testid={`settings-registry-schema-cancel-${entry.code}`}>
                                        {t('registry.action.cancel')}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="text-xs text-muted-foreground hover:underline"
                                onClick={() => setEditingSchema(true)}
                                data-testid={`settings-registry-schema-edit-${entry.code}`}
                            >
                                {entry.jsonschema != null ? t('registry.action.edit_schema_set') : t('registry.action.edit_schema')}
                            </button>
                        )}
                    </div>
                )}

                <AlertDialog open={confirmClearServer} onOpenChange={setConfirmClearServer}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>{t('registry.editor.clear_confirm_title')}</AlertDialogTitle>
                            <AlertDialogDescription>
                                {t('registry.editor.clear_confirm_description', { code: entry.code })}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel data-testid={`settings-registry-server-clear-cancel-${entry.code}`}>
                                {t('registry.action.cancel')}
                            </AlertDialogCancel>
                            <AlertDialogAction
                                onClick={handleClearServer}
                                data-testid={`settings-registry-server-clear-confirm-${entry.code}`}
                            >
                                {t('registry.editor.use_default')}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </TableCell>
            <TableCell className="text-right">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onViewOverrides}
                    disabled={entry.org_overrides === 0}
                    data-testid={`settings-registry-overrides-${entry.code}`}
                    aria-label={t('registry.action.view_overrides')}
                    title={t('registry.action.view_overrides')}
                >
                    {entry.org_overrides}
                </Button>
            </TableCell>
            <TableCell>
                {stale ? (
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Badge variant="warning" data-testid={`settings-registry-stale-${entry.code}`}>
                                    {t('registry.stale_badge')}
                                </Badge>
                            </TooltipTrigger>
                            <TooltipContent>{t('registry.stale_tooltip')}</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                ) : (
                    <span className="text-xs text-muted-foreground" data-testid={`settings-registry-last-read-${entry.code}`}>
                        {entry.last_read_at ? new Date(entry.last_read_at).toLocaleDateString(i18n.language) : t('registry.never_read')}
                    </span>
                )}
            </TableCell>
        </TableRow>
    )
}
